import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import IconTask from '@/icons/Task.svg';
import {
  useCompleteQualityControl,
  useStartQualityControlServiceAdjustment,
} from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { getQualityControllers } from '@/routes/Requests/New/components/ConditionForm/getQualityControllers';

import { ArticleActionProps } from './ArticleActions';
import { QualityControlKOChoiceDialog } from './QualityControlKOChoiceDialog';

export const ControlQualityAction = ({ article, onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();
  const { state, view, hasError, qualityControlFormData } = useArticleContext();
  const { request, workflow } = useRequestContext();

  const { mutateAsync: complete, isPending: isPendingCompleteQualityControl } =
    useCompleteQualityControl({
      articleId: article.id,
    });

  const { mutateAsync: startServiceAdjustment, isPending: isPendingStartServiceAdjustment } =
    useStartQualityControlServiceAdjustment({
      articleId: article.id,
    });

  const [apiError, setApiError] = useState<string | null>(null);
  const [isQualityControlIssueDialogOpen, setIsQualityControlIssueDialogOpen] = useState(false);

  const editableArticleConditionForm = qualityControlFormData?.articleConditionForms?.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const hasAnsweredNo = editableArticleConditionForm?.articleConditions?.filter(
    (condition) => condition?.answerData?.yesNoRadioValue === 'no'
  )?.length;

  const hasBlockingAnswer = qualityControlFormData?.conditionForm.questions.filter((question) =>
    question?.answers?.find(
      (answer) =>
        answer.makesQualityControlKO &&
        editableArticleConditionForm?.articleConditions.find(
          (articleCondition) => articleCondition.questionId === question.id
        )?.answerId === answer.id
    )
  ).length;

  const currentNodeNextQualityControl = workflow?.nodes
    .filter((node) => 'step' in node && node.step === 'quality-control')
    .find((node) => node.id === article.step?.id)?.next;

  const hasKOTypeChoice =
    Object.keys(currentNodeNextQualityControl || {}).filter((key) => key.startsWith('ko-'))
      .length === 2;

  const handleCompleteQualityControl = async (
    data: Endpoints['POST /articles/:id/complete-quality-control']['body']
  ) => {
    try {
      await complete(data);
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    } finally {
      setIsQualityControlIssueDialogOpen(false);
    }
  };

  const completeQualityControl = () => {
    const { qualityControllers } = getQualityControllers(article, request);
    const { hasError } = state.errors.check({
      qualityControl: {
        noAnswerer: qualityControllers.length > 0,
        repairedPhoto: view.qualityControl.photos.required,
        questions: {
          noAnswers: true,
          missingContext: true,
          missingPhoto: view.qualityControl.photos.required,
        },
        actionQuestions: {
          noAnswer: true,
          missingContext: true,
          missingPhoto: view.qualityControl.photos.required,
        },
      },
      details: {
        missingWeight: view.details.weight.shown && view.details.weight.required,
      },
    });

    setApiError(null);

    if (hasError) {
      return;
    }

    if (hasAnsweredNo || hasBlockingAnswer) {
      setIsQualityControlIssueDialogOpen(true);
    } else {
      handleCompleteQualityControl({});
    }
  };

  const handleStartServiceAdjustment = async () => {
    try {
      await startServiceAdjustment();
      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const showAdjustServiceButton =
    article.step?.step === 'quality-control' &&
    !!article.step.config.legacyServiceAdjustmentEnabled &&
    article.workshop?.workshopConfig?.nonDigitalized &&
    article.steps?.some((step) => step.status === 'done' && step.step === 'processing');

  return (
    <>
      <Footer>
        {(hasError || apiError) && (
          <FooterError>
            {hasError ? (
              <Trans id="article.actions.quality_control.errors">
                Please fill the missing information in order to complete the quality control
              </Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}
        <FooterButtons>
          {showAdjustServiceButton && (
            <Button
              variant="secondary"
              size="medium"
              onPress={handleStartServiceAdjustment}
              isLoading={isPendingStartServiceAdjustment}
              disabled={isPendingCompleteQualityControl}
            >
              <IconTask />
              <Trans id="article.actions.quality_control.adjust">Adjust services and actions</Trans>
            </Button>
          )}
          <Button
            variant="primary"
            size="medium"
            onPress={completeQualityControl}
            isLoading={isPendingCompleteQualityControl}
            disabled={isPendingStartServiceAdjustment}
          >
            <Trans id="article.actions.quality_control.validate">Complete quality control</Trans>
          </Button>
        </FooterButtons>
      </Footer>
      <QualityControlKOChoiceDialog
        isOpen={isQualityControlIssueDialogOpen}
        onClose={() => setIsQualityControlIssueDialogOpen(false)}
        onConfirm={handleCompleteQualityControl}
        withChoice={hasKOTypeChoice}
        isLoading={isPendingCompleteQualityControl}
      />
    </>
  );
};
